import { LogManager } from '@src/utility/Logger';
import { convertTimeToMMSS } from '@src/utility/timeFormat';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled, { css } from 'styled-components';

import Modal from '../../common/Modal';
import ClipSequenceList from './ClipSequenceList';
import { extractOrderedClipFragments, getBeatKey } from './clipSequenceUtils';
import { tryParsePartialOutline } from './outlineParser';
import OutlineViewerPanel from './OutlineViewerPanel';
import ReelOutlineComposer from './ReelOutlineComposer';
import ReelPreviewPlayer from './ReelPreviewPlayer';
import ReelTranscriptPanel from './ReelTranscriptPanel';
import { useReelClipGeneration } from './useReelClipGeneration';
import { useReelSave } from './useReelSave';

import type { ReelOutline, ReelProducerConfig } from './types';

const logger = LogManager.getLogger('ReelProducer:ReelProducerLayout');

type ReelPhase = 'outline' | 'composition';
type LeftPanelView = 'outline' | 'clips' | 'preview' | 'transcript';

type Props = {
  config: ReelProducerConfig;
  onClose: () => void;
};

const ReelProducerLayout: React.FC<Props> = ({ config, onClose }) => {
  // PLAT-11543 (audio-first): saving a reel works before renditions exist — it
  // is saved as story-ref + transcript ranges and resolves its video from the
  // story's renditions at open. So Save Reel is not gated on rendition
  // readiness (see saveProducedClips / buildSourceVideoFromStory).
  const [phase, setPhase] = useState<ReelPhase>('outline');
  const [leftPanelView, setLeftPanelView] = useState<LeftPanelView>('outline');
  const [outline, setOutline] = useState<ReelOutline | null>(null);
  const [excludedBeats, setExcludedBeats] = useState<Set<string>>(new Set());
  const [streamingText, setStreamingText] = useState('');
  const [isOutlineLoading, setIsOutlineLoading] = useState(false);

  const handleStreamingChange = useCallback(
    (rawResponse: string, isLoading: boolean) => {
      setStreamingText(rawResponse);
      setIsOutlineLoading(isLoading);
    },
    [],
  );

  // Parse partial outline during streaming so the title appears in the header early.
  // Cache the last successful title so it doesn't flicker back to placeholder between chunks.
  const lastStreamingTitleRef = useRef<string | null>(null);
  const parsedStreamingTitle = useMemo(() => {
    if (!isOutlineLoading || !streamingText) return null;
    const partial = tryParsePartialOutline(streamingText);
    return partial?.title ?? null;
  }, [isOutlineLoading, streamingText]);

  useEffect(() => {
    if (!isOutlineLoading) {
      lastStreamingTitleRef.current = null;
    } else if (parsedStreamingTitle) {
      lastStreamingTitleRef.current = parsedStreamingTitle;
    }
  }, [isOutlineLoading, parsedStreamingTitle]);
  const streamingTitle = isOutlineLoading
    ? (parsedStreamingTitle ?? lastStreamingTitleRef.current)
    : null;

  const clipGeneration = useReelClipGeneration();
  const reelSave = useReelSave();

  const handleToggleBeat = useCallback((beatKey: string) => {
    setExcludedBeats(prev => {
      const next = new Set(prev);
      if (next.has(beatKey)) {
        next.delete(beatKey);
      } else {
        next.add(beatKey);
      }
      return next;
    });
  }, []);

  const handleOutlineChange = useCallback((newOutline: ReelOutline | null) => {
    setOutline(newOutline);
  }, []);

  const [approvedOutlineRef, setApprovedOutlineRef] = useState<string | null>(
    null,
  );

  const handleApproveOutline = useCallback(() => {
    if (!outline) return;
    logger.info('Outline approved, starting clip generation', {
      outlineTitle: outline.title,
      chapters: outline.chapters.length,
    });
    setApprovedOutlineRef(JSON.stringify(outline));
    setPhase('composition');
    setLeftPanelView('clips');
    clipGeneration.generate(outline, config);
  }, [outline, config, clipGeneration]);

  const handleReprocess = useCallback(() => {
    if (!outline) return;
    logger.info('Reprocessing clips with updated outline', {
      outlineTitle: outline.title,
      chapters: outline.chapters.length,
    });
    setApprovedOutlineRef(JSON.stringify(outline));
    setExcludedBeats(new Set());
    clipGeneration.generate(outline, config);
  }, [outline, config, clipGeneration]);

  const outlineHasChanged = useMemo(() => {
    return (
      phase === 'composition' &&
      outline &&
      approvedOutlineRef !== null &&
      JSON.stringify(outline) !== approvedOutlineRef
    );
  }, [phase, outline, approvedOutlineRef]);

  const handleSaveReel = () => {
    if (!outline || !clipGeneration.results) return;
    logger.info('Save reel clicked');
    reelSave.saveReel({
      outline,
      clipResults: clipGeneration.results,
      excludedBeats,
      config,
      onComplete: onClose,
    });
  };

  // Auto-exclude beats that have no resolved clip fragment
  useEffect(() => {
    const results = clipGeneration.results;
    if (!results || clipGeneration.isGenerating) return;

    const unresolvedKeys: string[] = [];
    for (const [key, result] of results) {
      const selected = result.options[result.selectedOptionIndex];
      if (!selected?.clipFragment) {
        unresolvedKeys.push(key);
      }
    }

    if (unresolvedKeys.length > 0) {
      setExcludedBeats(prev => {
        const next = new Set(prev);
        for (const key of unresolvedKeys) {
          next.add(key);
        }
        return next;
      });
    }
  }, [clipGeneration.results, clipGeneration.isGenerating]);

  const reelStats = useMemo(() => {
    if (!outline) return null;
    const chapterCount = outline.chapters.length;
    const clipCount = outline.chapters.reduce(
      (sum, ch) => sum + ch.beats.length,
      0,
    );

    const results = clipGeneration.results;
    const hasClips = !!results && !clipGeneration.isGenerating;

    // Compute actual total duration from resolved clips (excluding excluded beats)
    let actualDuration: number | null = null;
    if (hasClips) {
      actualDuration = 0;
      for (const chapter of outline.chapters) {
        for (const beat of chapter.beats) {
          const key = getBeatKey(chapter, beat);
          if (excludedBeats.has(key)) continue;
          const result = results.get(key);
          const selected = result
            ? result.options[result.selectedOptionIndex]
            : null;
          actualDuration +=
            selected?.clipFragment?.duration ?? beat.estimatedDuration;
        }
      }
    }

    return { totalDuration: actualDuration, chapterCount, clipCount };
  }, [
    outline,
    clipGeneration.results,
    clipGeneration.isGenerating,
    excludedBeats,
  ]);

  const canSaveReel = useMemo(() => {
    if (
      !outline ||
      !clipGeneration.results ||
      clipGeneration.isGenerating ||
      reelSave.isSaving
    ) {
      return false;
    }
    const fragments = extractOrderedClipFragments(
      outline,
      clipGeneration.results,
      excludedBeats,
    );
    return fragments.length > 0;
  }, [
    outline,
    clipGeneration.results,
    clipGeneration.isGenerating,
    reelSave.isSaving,
    excludedBeats,
  ]);

  return (
    <Modal
      isOpen
      closeModal={onClose}
      blurBackground
      disableEscapeClose
      closeButtonStyle={{ top: '8px', right: '8px', zIndex: 20 }}
    >
      <ModalWrapper data-testid="reel-producer-layout">
        <TopHeaderBar>
          <TitleRow>
            {outline?.title ? (
              <ReelTitleBar
                contentEditable
                suppressContentEditableWarning
                onBlur={(e: React.FocusEvent<HTMLSpanElement>) => {
                  const newTitle = e.currentTarget.textContent?.trim();
                  if (newTitle && newTitle !== outline.title) {
                    setOutline({ ...outline, title: newTitle });
                  }
                }}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    (e.target as HTMLElement).blur();
                  }
                }}
                title="Click to edit reel title"
              >
                {outline.title}
              </ReelTitleBar>
            ) : streamingTitle ? (
              <ReelTitleBar as="span">{streamingTitle}</ReelTitleBar>
            ) : (
              <ReelTitlePlaceholder>Reel Producer</ReelTitlePlaceholder>
            )}
            <HeaderActions>
              <SaveReelButton
                disabled={!canSaveReel}
                onClick={handleSaveReel}
                data-testid="reel-save-button"
              >
                {reelSave.isSaving ? 'Saving...' : 'Save Reel'}
              </SaveReelButton>
            </HeaderActions>
          </TitleRow>
        </TopHeaderBar>
        <LayoutContainer>
          <MainPanel>
            <TabRow>
              <TabBar>
                <TabButton
                  $active={leftPanelView === 'outline'}
                  onClick={() => setLeftPanelView('outline')}
                  data-testid="reel-tab-outline"
                >
                  Outline
                </TabButton>
                {phase === 'composition' && (
                  <TabButton
                    $active={leftPanelView === 'clips'}
                    onClick={() => setLeftPanelView('clips')}
                    data-testid="reel-tab-clips"
                  >
                    Clips
                  </TabButton>
                )}
                {clipGeneration.results && !clipGeneration.isGenerating && (
                  <>
                    <TabButton
                      $active={leftPanelView === 'preview'}
                      onClick={() => setLeftPanelView('preview')}
                      data-testid="reel-tab-preview"
                    >
                      Preview
                    </TabButton>
                    <TabButton
                      $active={leftPanelView === 'transcript'}
                      onClick={() => setLeftPanelView('transcript')}
                      data-testid="reel-tab-transcript"
                    >
                      Transcript
                    </TabButton>
                  </>
                )}
              </TabBar>
              <StatsRow>
                {reelStats ? (
                  <>
                    {reelStats.totalDuration !== null && (
                      <>
                        <StatItem>
                          {convertTimeToMMSS(
                            Math.round(reelStats.totalDuration),
                          )}{' '}
                          Total
                        </StatItem>
                        <StatDot>&middot;</StatDot>
                      </>
                    )}
                    <StatItem>
                      {reelStats.chapterCount}{' '}
                      {reelStats.chapterCount === 1 ? 'Chapter' : 'Chapters'}
                    </StatItem>
                    <StatDot>&rarr;</StatDot>
                    <StatItem>
                      {reelStats.clipCount}{' '}
                      {reelStats.clipCount === 1 ? 'Clip' : 'Clips'}
                    </StatItem>
                  </>
                ) : null}
              </StatsRow>
            </TabRow>
            <PanelContent $noScroll={leftPanelView === 'preview'}>
              {leftPanelView === 'outline' && (
                <OutlineViewerPanel
                  outline={outline}
                  streamingText={isOutlineLoading ? streamingText : undefined}
                  isOutlineLoading={isOutlineLoading}
                  onApprove={
                    phase === 'outline' ? handleApproveOutline : undefined
                  }
                  onReprocess={outlineHasChanged ? handleReprocess : undefined}
                  canApprove={phase === 'outline' && !!outline}
                  canReprocess={!!outlineHasChanged}
                  isProcessing={clipGeneration.isGenerating}
                />
              )}
              {leftPanelView === 'clips' && (
                <>
                  {clipGeneration.isGenerating && !clipGeneration.results && (
                    <PreviewPlaceholder>
                      Generating clips for your reel...
                    </PreviewPlaceholder>
                  )}
                  {clipGeneration.error && (
                    <PreviewPlaceholder>
                      Clip generation failed: {clipGeneration.error}
                    </PreviewPlaceholder>
                  )}
                  {clipGeneration.results && outline && (
                    <ClipSequenceList
                      outline={outline}
                      clipResults={clipGeneration.results}
                      onOutlineChange={setOutline}
                      excludedBeats={excludedBeats}
                      onToggleBeat={handleToggleBeat}
                    />
                  )}
                  {!clipGeneration.isGenerating &&
                    !clipGeneration.error &&
                    !clipGeneration.results && (
                      <PreviewPlaceholder>
                        Approve an outline to generate clips
                      </PreviewPlaceholder>
                    )}
                </>
              )}
              {leftPanelView === 'preview' &&
                clipGeneration.results &&
                outline && (
                  <ReelPreviewPlayer
                    outline={outline}
                    clipResults={clipGeneration.results}
                    excludedBeats={excludedBeats}
                    onOutlineChange={setOutline}
                  />
                )}
              {leftPanelView === 'transcript' &&
                clipGeneration.results &&
                outline && (
                  <ReelTranscriptPanel
                    outline={outline}
                    clipResults={clipGeneration.results}
                    excludedBeats={excludedBeats}
                  />
                )}
            </PanelContent>
          </MainPanel>
          <ChatPanel>
            <ChatContent>
              <ReelOutlineComposer
                config={config}
                onOutlineChange={handleOutlineChange}
                onStreamingChange={handleStreamingChange}
              />
            </ChatContent>
          </ChatPanel>
        </LayoutContainer>
      </ModalWrapper>
    </Modal>
  );
};

ReelProducerLayout.displayName = 'ReelProducerLayout';

export default ReelProducerLayout;

// --- Styled Components ---

const ModalWrapper = styled.div`
  position: relative;
  border-radius: 10px;
  background-color: var(--background);
  width: 90vw;
  max-width: 1200px;
  height: 90vh;
  max-height: calc(100vh - 80px);
  border: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const TopHeaderBar = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid var(--border);
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid var(--border);
`;

const TabRow = styled.div`
  display: flex;
  align-items: center;
  padding: 0 16px;
  border-bottom: 1px solid var(--border);
`;

const ReelTitlePlaceholder = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: var(--text-muted);
`;

const TabBar = styled.div`
  display: flex;
  gap: 0 var(--spacing-lg, 24px);
  align-items: center;
  flex-shrink: 0;
`;

const ReelTitleBar = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: var(--foreground);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 300px;
  cursor: text;
  padding: 2px 6px;
  border-radius: 4px;
  border: 1px solid transparent;
  transition: border-color 150ms ease;
  outline: none;

  &:hover {
    border-color: var(--border);
  }

  &:focus {
    border-color: var(--accent);
    white-space: normal;
    overflow: visible;
  }
`;

const StatsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: var(--text-secondary);
  margin-left: auto;
`;

const StatItem = styled.span`
  white-space: nowrap;
  font-weight: 500;
`;

const StatDot = styled.span`
  color: var(--text-muted);
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  padding-right: 32px;
`;

const SaveReelButton = styled.button<{ disabled: boolean }>`
  padding: 8px 20px;
  border-radius: 8px;
  border: 1px solid var(--primary);
  background: var(--primary);
  color: var(--background);
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 150%;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${props => (props.disabled ? 0.5 : 1)};
  transition: all 150ms ease;

  &:hover {
    ${props => !props.disabled && 'opacity: 0.9;'}
  }
`;

const LayoutContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  min-height: 0;
  gap: 0;
`;

const MainPanel = styled.div`
  flex: 1;
  min-width: 0;
  border-right: 1px solid var(--border);
  display: flex;
  flex-direction: column;
`;

const TabButton = styled.button<{
  $active: boolean;
  $disabled?: boolean;
}>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: var(--spacing-sm, 8px) 0;
  margin: 0;
  border: none;
  border-radius: 0;
  background: transparent;
  font-family: var(--font-primary, Inter, sans-serif);
  font-size: var(--text-sm, 14px);
  font-weight: var(--font-weight-normal, 400);
  color: var(--text-secondary);
  white-space: nowrap;
  cursor: ${props => (props.$disabled ? 'not-allowed' : 'pointer')};
  opacity: ${props => (props.$disabled ? 0.5 : 1)};
  transition: color 0.2s ease;

  &:hover {
    color: var(--foreground);
  }

  ${props =>
    props.$active &&
    css`
      color: var(--foreground);
      font-weight: var(--font-weight-medium, 500);

      &::after {
        content: '';
        position: absolute;
        bottom: -1px;
        left: 0;
        right: 0;
        height: var(--spacing-2xs, 3px);
        background-color: var(--accent);
        border-radius: 1px;
      }
    `}
`;

const PanelContent = styled.div<{ $noScroll?: boolean }>`
  flex: 1;
  overflow-y: ${props => (props.$noScroll ? 'hidden' : 'auto')};
  min-height: 0;
  ${props => props.$noScroll && 'display: flex; flex-direction: column;'}
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const ChatPanel = styled.div`
  flex: 0 0 350px;
  min-width: 280px;
  max-width: 400px;
  display: flex;
  flex-direction: column;
`;

const ChatContent = styled.div`
  flex: 1;
  overflow: hidden;
  min-height: 0;
`;

const PreviewPlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  text-align: center;
  color: var(--text-muted);
  font-size: var(--text-xs);
  padding: 16px;
`;
